import { alpha } from '@mui/material/styles';
import { DASHBOARD_ACCENT, getDashboardDarkSurfaceTokens } from './dashboardSurfaceSx';
import { mobileAppGlassCard } from '../components/layout/mobile/mobileAppTheme';

/** Sticky channel / room-type column — fits image + short code on mobile. */
export const MOBILE_INVENTORY_LABEL_WIDTH = 70;
export const MOBILE_INVENTORY_DAY_COUNT = 5;
export const DESKTOP_INVENTORY_DAY_COUNT = 10;
export const INVENTORY_DATE_COL_MIN_WIDTH = 80;
const WEEKEND_TINT = '#f59e0b';

export function isWeekendDate(date) {
  const day = typeof date?.day === 'function' ? date.day() : null;
  return day === 0 || day === 6;
}

export function inventoryWeekendWash(isDark) {
  return isDark ? alpha(WEEKEND_TINT, 0.12) : '#fff6e5';
}

export function inventoryThemeTokens(isDark) {
  return getDashboardDarkSurfaceTokens(isDark);
}

export function inventoryVisibleDayCount(isMobile) {
  return isMobile ? MOBILE_INVENTORY_DAY_COUNT : DESKTOP_INVENTORY_DAY_COUNT;
}

export function inventoryGridColumns(isMobile, dateCount, desktopLabelWidth = 200) {
  if (isMobile) {
    return `${MOBILE_INVENTORY_LABEL_WIDTH}px repeat(${dateCount}, minmax(58px, 1fr))`;
  }
  const dateCol = `minmax(${INVENTORY_DATE_COL_MIN_WIDTH}px, 1fr)`;
  return {
    xs: `120px repeat(${dateCount}, ${dateCol})`,
    sm: `150px repeat(${dateCount}, ${dateCol})`,
    md: `${desktopLabelWidth}px repeat(${dateCount}, ${dateCol})`,
  };
}

export function inventoryGridGap(isMobile) {
  return isMobile ? 0.375 : { xs: 0.5, sm: 1 };
}

export function inventoryGridRowSx(isMobile, dateCount, desktopLabelWidth = 200, extra = {}) {
  return {
    display: 'grid',
    gridTemplateColumns: inventoryGridColumns(isMobile, dateCount, desktopLabelWidth),
    gap: inventoryGridGap(isMobile),
    width: isMobile ? '100%' : 'max-content',
    minWidth: isMobile ? undefined : '100%',
    ...extra,
  };
}

/** Wraps date headers + rate rows — parent scrolls horizontally; rows stay column-aligned. */
export function inventoryGridScrollInnerSx(isMobile) {
  if (isMobile) {
    return { width: '100%', minWidth: 0 };
  }
  return {
    width: 'max-content',
    minWidth: '100%',
  };
}

export function inventoryStickyLabelCellSx(isMobile, isDark, options = {}) {
  const tokens = inventoryThemeTokens(isDark);
  const { accent = false, roomType = false } = options;

  return {
    p: isMobile ? 0.375 : { xs: 0.5, sm: 1 },
    pl: roomType && !isMobile ? { xs: 1.5, sm: 2 } : undefined,
    display: 'flex',
    alignItems: 'center',
    justifyContent: isMobile ? 'center' : 'space-between',
    flexDirection: isMobile && roomType ? 'column' : 'row',
    gap: isMobile ? 0.25 : 1,
    borderRight: `1px solid ${tokens.surfaceBorder}`,
    backgroundColor: accent
      ? (isDark ? 'rgba(76, 225, 182, 0.15)' : 'rgba(76, 225, 182, 0.1)')
      : (isDark ? tokens.surface : '#ffffff'),
    color: accent ? DASHBOARD_ACCENT : tokens.text,
    position: 'sticky',
    left: 0,
    zIndex: accent ? 3 : 2,
    minWidth: 0,
    overflow: 'hidden',
    ...(accent ? { borderBottom: `2px solid ${DASHBOARD_ACCENT}`, fontWeight: 600 } : {}),
    ...(roomType && !isMobile ? { borderLeft: `3px solid ${DASHBOARD_ACCENT}` } : {}),
    ...(isMobile
      ? {
          minWidth: MOBILE_INVENTORY_LABEL_WIDTH,
          maxWidth: MOBILE_INVENTORY_LABEL_WIDTH,
        }
      : {}),
  };
}

export function inventoryChannelCornerCellSx(isDark) {
  const tokens = inventoryThemeTokens(isDark);
  return {
    backgroundColor: isDark ? tokens.surface : '#ffffff',
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    p: { xs: 0.5, sm: 1 },
    borderRight: `1px solid ${tokens.surfaceBorder}`,
    position: 'sticky',
    left: 0,
    zIndex: 3,
    minWidth: 0,
    overflow: 'hidden',
  };
}

export function inventoryDateHeaderCellSx(isMobile, isDark, isToday, isWeekend = false) {
  const tokens = inventoryThemeTokens(isDark);
  const weekendBg = inventoryWeekendWash(isDark);
  const weekendBorder = isDark ? alpha(WEEKEND_TINT, 0.32) : '#f3d5a0';

  if (isMobile) {
    return {
      textAlign: 'center',
      p: 0.375,
      minHeight: 44,
      height: 44,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      borderRadius: '10px',
      border: isToday
        ? `1px solid ${alpha(DASHBOARD_ACCENT, 0.65)}`
        : `1px solid ${isWeekend ? weekendBorder : tokens.surfaceBorder}`,
      background: isToday
        ? `linear-gradient(145deg, ${alpha(DASHBOARD_ACCENT, 0.35)} 0%, ${alpha(DASHBOARD_ACCENT, 0.12)} 100%)`
        : isWeekend
          ? weekendBg
          : (isDark ? 'rgba(255,255,255,0.04)' : '#ffffff'),
      color: isToday ? '#f8fafc' : isWeekend ? (isDark ? '#fbbf24' : '#b45309') : tokens.text,
    };
  }

  return {
    textAlign: 'center',
    p: { xs: 0.5, sm: 1 },
    minWidth: INVENTORY_DATE_COL_MIN_WIDTH,
    flexShrink: 0,
    backgroundColor: isToday
      ? (isDark ? alpha(DASHBOARD_ACCENT, 0.22) : alpha(DASHBOARD_ACCENT, 0.12))
      : isWeekend
        ? weekendBg
        : (isDark ? tokens.surface : '#ffffff'),
    color: isToday
      ? (isDark ? '#f8fafc' : '#0f172a')
      : isWeekend
        ? (isDark ? '#fbbf24' : '#b45309')
        : tokens.text,
    borderRadius: 1,
    minHeight: { xs: '50px', sm: '60px' },
    height: { xs: '50px', sm: '60px' },
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    border: `1px solid ${
      isToday
        ? alpha(DASHBOARD_ACCENT, isDark ? 0.45 : 0.35)
        : isWeekend
          ? weekendBorder
          : tokens.surfaceBorder
    }`,
  };
}

export function inventoryDateHeaderLabelSx(isMobile) {
  return {
    fontSize: isMobile ? '0.56rem' : { xs: '0.65rem', sm: '0.75rem' },
    lineHeight: 1.1,
    textTransform: isMobile ? 'uppercase' : 'none',
    letterSpacing: isMobile ? '0.04em' : 'normal',
    opacity: isMobile ? 0.85 : 1,
  };
}

export function inventoryDateHeaderDaySx(isMobile, isToday) {
  return {
    fontWeight: 700,
    fontSize: isMobile ? '0.82rem' : { xs: '0.875rem', sm: '1rem' },
    lineHeight: 1.1,
    color: isMobile && isToday ? DASHBOARD_ACCENT : 'inherit',
  };
}

export function inventoryRateCellFillSx(isDark, isSelected = false, isWeekend = false) {
  const tokens = inventoryThemeTokens(isDark);
  const restBg = isWeekend
    ? inventoryWeekendWash(isDark)
    : (isDark ? tokens.surface : '#ffffff');
  return {
    // Column template owns width (desktop minmax / mobile fr). Cell minWidth was
    // clipping mobile content and making rates look right-aligned.
    width: '100%',
    minWidth: 0,
    maxWidth: '100%',
    boxSizing: 'border-box',
    backgroundColor: isSelected
      ? (isDark ? 'rgba(76, 225, 182, 0.15)' : 'rgba(76, 225, 182, 0.08)')
      : restBg,
    '&:hover': {
      backgroundColor: isDark ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.04)',
    },
  };
}

export function inventoryDrawerHeaderSx(isDark) {
  const tokens = inventoryThemeTokens(isDark);
  return {
    borderBottom: `2px solid ${tokens.surfaceBorderStrong}`,
    backgroundColor: tokens.headerBg,
  };
}

export function inventoryDrawerFooterSx(isDark) {
  const tokens = inventoryThemeTokens(isDark);
  return {
    borderTop: `2px solid ${tokens.surfaceBorderStrong}`,
    backgroundColor: tokens.headerBg,
  };
}

export function inventoryMenuPaperSx(isDark, extra = {}) {
  const tokens = inventoryThemeTokens(isDark);
  return {
    background: isDark ? tokens.surfaceGradient : '#ffffff',
    backgroundColor: isDark ? tokens.surface : '#ffffff',
    border: `1px solid ${tokens.surfaceBorder}`,
    boxShadow: isDark ? '0 18px 36px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.04)' : undefined,
    ...extra,
  };
}

export function inventoryMobileFiltersSx() {
  return {
    ...mobileAppGlassCard,
    p: 1,
    mb: 1,
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 1,
    width: '100%',
  };
}

export function inventoryMobileToolbarFilterRowSx() {
  return {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 1,
    width: '100%',
  };
}

export function inventoryMobileToolbarSx() {
  return {
    ...mobileAppGlassCard,
    p: 1.25,
    mb: 1.5,
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
  };
}

export function inventoryMobileGridCornerSx(isDark) {
  return {
    ...inventoryStickyLabelCellSx(true, isDark),
    justifyContent: 'center',
    zIndex: 2,
    minHeight: 44,
    height: 44,
  };
}

export function inventoryDataCellSx(isMobile, baseSx = {}) {
  if (!isMobile) return baseSx;
  return {
    ...baseSx,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    boxSizing: 'border-box',
    width: '100%',
    minWidth: 0,
    maxWidth: '100%',
    minHeight: 42,
    height: 42,
    p: 0.375,
    overflow: 'hidden',
    '& .MuiTypography-root': {
      width: '100%',
      maxWidth: '100%',
      textAlign: 'center',
      whiteSpace: 'nowrap',
      fontVariantNumeric: 'tabular-nums',
      lineHeight: 1.1,
    },
  };
}

export function inventoryStatusDotSx(isMobile, color) {
  return {
    position: 'absolute',
    // Keep the status marker under the centered value on mobile so it doesn't
    // pull the rate/inventory number visually to the right.
    bottom: isMobile ? 2 : 2,
    left: isMobile ? '50%' : 2,
    transform: isMobile ? 'translateX(-50%)' : 'none',
    width: isMobile ? 4 : 6,
    height: isMobile ? 4 : 6,
    borderRadius: '50%',
    backgroundColor: color,
    boxShadow: color ? `0 0 3px ${color}` : undefined,
    pointerEvents: 'none',
  };
}
